import PropTypes from 'prop-types'
import { useState } from 'react';
import {
  Stack,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography
} from '@mui/material'
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import 'utils/validations';

import Dialog from 'components/Dialog';
import TextField from 'components/TextField';
import {
  AcceptInviteFormStepCompany,
  AcceptInviteFormStepCompanyAddress,
  AcceptInviteFormStepPersonal,
  AcceptInviteFormStepPersonalAddress,
  AcceptInviteFormStepPersonalDocument
} from 'sections/auth';

import { createAdmin } from 'services/requests/superuser';
import { removeMask, toast } from 'utils';

const steps = [
  'Dados da Empresa',
  'Endereço da Empresa',
  'Dados do Responsável',
  'Endereço do Responsável',
  'Documentos',
  'Senha'
];

const validationSchemas = [
  yup.object().shape({
    socialName: yup.string().required('Obrigatório'),
    fantasyName: yup.string().required('Obrigatório'),
    emailCompany: yup.string().email('O email deve ser válido').required('Obrigatório'),
    phoneCompany: yup.string()
      .phone('O telefone deve ser válido')
      .required('Obrigatório'),
    sacCompany: yup.string()
      .phone('O telefone SAC deve ser válido')
      .required('Obrigatório'),
    cnpj: yup.string()
      .cnpj('O CNPJ deve ser válido')
      .required('Obrigatório'),
    pixType: yup.string().required('Obrigatório'),
    pix: yup.string().required('Obrigatório')
  }),
  yup.object().shape({
    zipcodeCompany: yup.string()
      .cep('O CEP deve ser válido')
      .required('Obrigatório'),
    streetCompany: yup.string().required('Obrigatório'),
    neighborhoodCompany: yup.string().required('Obrigatório'),
    cityCompany: yup.string().required('Obrigatório'),
    stateCompany: yup.string().required('Obrigatório'),
    numberCompany: yup.string().required('Obrigatório')
  }),
  yup.object().shape({
    name: yup.string().required('Obrigatório'),
    email: yup.string().email('O email deve ser válido').required('Obrigatório'),
    phone: yup.string()
      .phone('O telefone deve ser válido')
      .required('Obrigatório'),
    cpf: yup.string()
      .cpf('O CPF deve ser válido')
      .required('Obrigatório'),
    rg: yup.string().required('Obrigatório'),
    birthDate: yup.string()
      .birthdate('A data de nascimento deve ser válida')
      .required('Obrigatório'),
    profession: yup.string().required('Obrigatório'),
    nationality: yup.string().required('Obrigatório'),
    maritalStatus: yup.string().required('Obrigatório'),
    propertyRegime: yup.string().required('Obrigatório')
  }),
  yup.object().shape({
    zipcode: yup.string()
      .when('applyCompanyAddress', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.cep('O CEP deve ser válido').required('Obrigatório')
      }),
    street: yup.string()
      .when('applyCompanyAddress', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Obrigatório')
      }),
    neighborhood: yup.string()
      .when('applyCompanyAddress', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Obrigatório')
      }),
    city: yup.string()
      .when('applyCompanyAddress', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Obrigatório')
      }),
    state: yup.string()
      .when('applyCompanyAddress', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Obrigatório')
      }),
    number: yup.string()
      .when('applyCompanyAddress', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Obrigatório')
      })
    }),
  yup.object().shape({
    document: yup.array()
      .min(1, 'É necessário anexar pelo menos 1 documento')
      .max(5, 'Máximo de 5 documentos')
      .required('Obrigatório')
  }),
  yup.object().shape({
    password: yup.string()
      .password('A senha deve conter pelo menos 8 caracteres, contendo pelo menos 1 número, 1 caracter especial, uma letra maiúscula e uma letra minúscula')
      .required('Obrigatório')
  })
];

function _renderStepContent({ activeStep, ...props }) {
  switch (activeStep) {
    case 0:
      return (
        <AcceptInviteFormStepCompany
          hasSACField={true}
          values={props.values}
          handleChange={props.handleChange}
          touched={props.touched}
          errors={props.errors}
          setFieldValue={props.setFieldValue}
        />
      );
    case 1:
      return (
        <AcceptInviteFormStepCompanyAddress
          values={props.values}
          handleChange={props.handleChange}
          touched={props.touched}
          errors={props.errors}
          setFieldValue={props.setFieldValue}
        />
      );
    case 2:
      return (
        <AcceptInviteFormStepPersonal
          values={props.values}
          handleChange={props.handleChange}
          touched={props.touched}
          errors={props.errors}
          isEditing={true}
        />
      );
    case 3:
      return (
        <AcceptInviteFormStepPersonalAddress
          values={{
            ...props.values,
            applyCompanyAddress: props.values.applyCompanyAddress || false,
            zipcodeCompany: props.values.zipcodeCompany,
            streetCompany: props.values.streetCompany,
            neighborhoodCompany: props.values.neighborhoodCompany,
            cityCompany: props.values.cityCompany,
            stateCompany: props.values.stateCompany,
            numberCompany: props.values.numberCompany,
            complementCompany: props.values.complementCompany
          }}
          handleChange={props.handleChange}
          touched={props.touched}
          errors={props.errors}
          setFieldValue={props.setFieldValue}
          isEditing={false}
        />
      );
    case 4:
      return (
        <AcceptInviteFormStepPersonalDocument
          values={props.values}
          touched={props.touched}
          errors={props.errors}
          setFieldValue={props.setFieldValue}
        />
      );
    case 5:
      return (
        <Stack spacing={3}>
          <TextField
            fullWidth
            id="password"
            name="password"
            label={props.t('auth.acceptInvite.form.password')}
            type="password"
            value={props.values.password}
            onChange={props.handleChange}
            error={props.touched.password && Boolean(props.errors.password)}
            helperText={props.touched.password && props.errors.password || 'Mínimo 8 caracteres, com letras maiúsculas, minúsculas e números'}
          />
        </Stack>
      );
    default:
      return <div>Not Found</div>;
  }
}

export default function CreateAdminForm({ open, onCancel, onSuccess }) {
  const { t } = useTranslation('common');
  const [activeStep, setActiveStep] = useState(0);

  const currentValidationSchema = validationSchemas[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const initialValues = {
    name: '',
    email: '',
    phone: '',
    cpf: '',
    rg: '',
    birthDate: '',
    profession: '',
    nationality: 'Brasileiro',
    maritalStatus: '',
    propertyRegime: '',
    password: '',
    zipcode: '',
    street: '',
    neighborhood: '',
    city: '',
    state: '',
    number: '',
    complement: '',
    applyCompanyAddress: false,
    socialName: '',
    fantasyName: '',
    emailCompany: '',
    phoneCompany: '',
    sacCompany: '',
    cnpj: '',
    pixType: '',
    pix: '',
    zipcodeCompany: '',
    streetCompany: '',
    neighborhoodCompany: '',
    cityCompany: '',
    stateCompany: '',
    numberCompany: '',
    complementCompany: '',
    document: null
  };

  async function submitForm(values, actions) {
    try {
      const body = {
        user: {
          name: values.name,
          email: values.email.toLowerCase(),
          phone: removeMask(values.phone),
          cpf: removeMask(values.cpf),
          rg: values.rg,
          birthDate: values.birthDate.split('/').reverse().join('-'),
          profession: values.profession,
          nationality: values.nationality,
          maritalStatus: values.maritalStatus,
          propertyRegime: values.propertyRegime,
          password: values.password
        },
        userAddress: values.applyCompanyAddress ? {
          zipcode: removeMask(values.zipcodeCompany),
          street: values.streetCompany,
          neighborhood: values.neighborhoodCompany,
          city: values.cityCompany,
          state: values.stateCompany,
          number: values.numberCompany,
          complement: values.complementCompany || null
        } : {
          zipcode: removeMask(values.zipcode),
          street: values.street,
          neighborhood: values.neighborhood,
          city: values.city,
          state: values.state,
          number: values.number,
          complement: values.complement || null
        },
        company: {
          name: values.socialName,
          fantasyName: values.fantasyName,
          email: values.emailCompany.toLowerCase(),
          phone: removeMask(values.phoneCompany),
          sac: removeMask(values.sacCompany),
          cnpj: removeMask(values.cnpj),
          pixType: values.pixType,
          pix: (values.pixType === 'phone' || values.pixType === 'cnpj' || values.pixType === 'cpf') ? removeMask(values.pix) : values.pix
        },
        companyAddress: {
          zipcode: removeMask(values.zipcodeCompany),
          street: values.streetCompany,
          neighborhood: values.neighborhoodCompany,
          city: values.cityCompany,
          state: values.stateCompany,
          number: values.numberCompany,
          complement: values.complementCompany || null
        }
      };

      const formData = new FormData()
      if (values.document && values.document.length > 0) {
        values.document.forEach(file => {
          formData.append('file', file)
        })
      }
      formData.append('body', JSON.stringify(body))

      await createAdmin({ formData });
      toast('Administrador cadastrado com sucesso', 'success');
      handleReset();
      onSuccess?.();
    } catch (err) {
      toast(err, 'error');
    } finally {
      actions.setSubmitting(false);
    }
  }

  function handleSubmit(values, actions) {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  }

  function handleBack() {
    setActiveStep(activeStep - 1);
  }

  const handleReset = () => {
    setActiveStep(0);
    onCancel();
  }

  return (
    (open) &&
    <Dialog
      open={open}
      options={{
        title: 'Cadastrar Novo Administrador',
        content: (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ width: '100%', my: 3 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((_, key) => (
                  <Step key={key}>
                    <StepLabel />
                  </Step>
                ))}
              </Stepper>
              <Typography variant="subtitle1" sx={{ mt: 3, textAlign: 'center' }}>
                {steps[activeStep]}
              </Typography>
            </Box>

            <Formik
              initialValues={initialValues}
              validationSchema={currentValidationSchema}
              onSubmit={(values, actions) => handleSubmit(values, actions)}
            >
              {({ values, handleChange, touched, errors, setFieldValue, isSubmitting }) => (
                <Form>
                  {_renderStepContent({
                    activeStep,
                    values: {
                      ...values,
                      applyCompanyAddress: values.applyCompanyAddress || false,
                      socialName: values.socialName,
                      fantasyName: values.fantasyName,
                      emailCompany: values.emailCompany,
                      phoneCompany: values.phoneCompany,
                      cnpj: values.cnpj,
                      pixType: values.pixType,
                      pix: values.pix,
                      zipcodeCompany: values.zipcodeCompany,
                      streetCompany: values.streetCompany,
                      neighborhoodCompany: values.neighborhoodCompany,
                      cityCompany: values.cityCompany,
                      stateCompany: values.stateCompany,
                      numberCompany: values.numberCompany,
                      complementCompany: values.complementCompany
                    },
                    handleChange,
                    touched,
                    errors,
                    setFieldValue,
                    t
                  })}

                  <Stack sx={{ gap: 1, flexDirection: { xs: 'column-reverse', md: 'row' }, justifyContent: 'flex-end', alignItems: 'flex-end', width: '100%' }} mt={3}>
                    <Button
                      sx={{ justifyContent: 'center', width: { xs: '100%', md: 'auto' } }}
                      color="primary"
                      variant="outlined"
                      onClick={() => handleReset()}
                      size="large"
                    >
                      {t('cancel')}
                    </Button>
                    {activeStep !== 0 ? (
                      <Button
                        sx={{ justifyContent: 'center', width: { xs: '100%', md: 'auto' } }}
                        color="primary"
                        variant="contained"
                        onClick={() => handleBack()}
                        startIcon={<ArrowBack />}
                        size="large"
                      >
                        {t('back')}
                      </Button>
                    ) : <div />}
                    <LoadingButton
                      sx={{ justifyContent: 'center', width: { xs: '100%', md: 'auto' } }}
                      color="secondary"
                      variant="contained"
                      endIcon={isLastStep ? null : <ArrowForward />}
                      size="large"
                      type="submit"
                      loading={isSubmitting}
                    >
                      {isLastStep ? t('confirm') : t('next')}
                    </LoadingButton>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Box>
        ),
        hideConfirm: true,
        hideCancel: true,
        dialogProps: {
          maxWidth: 'sm'
        }
      }}
    />
  );
}

CreateAdminForm.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func
}
